import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { ApiResponse, AuthResponse, LoginCredentials, RegisterData } from '../types/saas';

class SaasApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: process.env.REACT_APP_SAAS_API_URL || 'http://localhost:5000/api',
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Interceptor para agregar token de autenticación
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('saas_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Interceptor para manejar respuestas
    this.api.interceptors.response.use(
      (response: AxiosResponse) => {
        return response;
      },
      (error) => {
        if (error.response?.status === 401) {
          // Token expirado o inválido
          localStorage.removeItem('saas_token');
          localStorage.removeItem('saas_user');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Métodos de autenticación
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await this.api.post<ApiResponse<AuthResponse>>('/auth/login', credentials);
    return response.data.data!;
  }

  async register(userData: RegisterData): Promise<AuthResponse> {
    const response = await this.api.post<ApiResponse<AuthResponse>>('/auth/register', userData);
    return response.data.data!;
  }

  async getProfile() {
    const response = await this.api.get<ApiResponse<{ user: any }>>('/auth/profile');
    return response.data.data!.user;
  }

  async logout() {
    await this.api.post('/auth/logout');
    localStorage.removeItem('saas_token');
    localStorage.removeItem('saas_user');
  }

  // Métodos para vehículos
  async getVehicles(params?: any) {
    const response = await this.api.get('/vehicles', { params });
    return response.data.data?.vehicles || response.data.data || response.data || [];
  }

  async getVehicle(id: number) {
    const response = await this.api.get(`/vehicles/${id}`);
    return response.data.data;
  }

  async createVehicle(data: FormData) {
    const response = await this.api.post('/vehicles', data, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data.data;
  }

  async updateVehicle(id: number, data: FormData) {
    // Algunos hostings bloquean PUT con multipart/form-data. Usamos POST con override.
    try {
      const response = await this.api.post(`/vehicles/${id}?_method=PUT`, data, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'X-HTTP-Method-Override': 'PUT'
        }
      });
      return response.data.data;
    } catch (err) {
      // Fallback: intenta PUT si el entorno sí lo permite
      const response = await this.api.put(`/vehicles/${id}`, data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data.data;
    }
  }

  async deleteVehicle(id: number) {
    const response = await this.api.delete(`/vehicles/${id}`);
    return response.data;
  }

  async getVehicleAlerts() {
    const response = await this.api.get('/vehicles/alerts');
    return response.data.data;
  }

  // Métodos para remolques (trailers)
  async getTrailers(params?: any) {
    const response = await this.api.get('/trailers', { params });
    return response.data.data?.trailers || response.data.data || response.data || [];
  }

  async getTrailer(id: number) {
    const response = await this.api.get(`/trailers/${id}`);
    return response.data.data;
  }

  async createTrailer(data: FormData) {
    const response = await this.api.post('/trailers', data, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data.data;
  }

  async updateTrailer(id: number, data: FormData) {
    try {
      // Usar solo el header de override (algunos WAF bloquean _method en query)
      const response = await this.api.post(`/trailers/${id}`, data, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'X-HTTP-Method-Override': 'PUT'
        }
      });
      return response.data.data;
    } catch (err) {
      const response = await this.api.put(`/trailers/${id}`, data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data.data;
    }
  }

  async deleteTrailer(id: number) {
    const response = await this.api.delete(`/trailers/${id}`);
    return response.data;
  }

  async getTrailerAlerts(params?: { days?: number }) {
    const response = await this.api.get('/trailers/alerts', { params });
    return response.data.data;
  }

  async getDriverAlerts(params?: { days?: number }) {
    const response = await this.api.get('/drivers/alerts', { params });
    return response.data.data;
  }

  // Finanzas - Ingresos
  async getFinancialSummary(params?: { start_date?: string; end_date?: string; vehicle_id?: number }) {
    const response = await this.api.get('/finance/summary', { params });
    return response.data.data;
  }

  async getRevenues(params?: any) {
    const response = await this.api.get('/finance/revenues', { params });
    return response.data.data?.revenues || response.data.data || [];
  }

  async createRevenue(data: any) {
    const response = await this.api.post('/finance/revenues', data);
    return response.data.data;
  }

  async updateRevenue(id: number, data: any) {
    const response = await this.api.put(`/finance/revenues/${id}`, data);
    return response.data.data;
  }

  async markRevenuePaid(id: number, payload?: { payment_date?: string; payment_method?: string }) {
    const response = await this.api.patch(`/finance/revenues/${id}/mark-paid`, payload || {});
  }

  // Finanzas - Gastos (por si se usa luego)
  async getExpenses(params?: any) {
    const response = await this.api.get('/finance/expenses', { params });
    return response.data.data?.expenses || response.data.data || response.data || [];
  }

  async deleteRevenue(id: number) {
    // Usar POST alias para evitar bloqueos DELETE
    const response = await this.api.post(`/finance/revenues/${id}/delete`);
    return response.data;
  }

  async deleteExpense(id: number) {
    const response = await this.api.post(`/finance/expenses/${id}/delete`);
    return response.data;
  }

  async createExpense(data: any) {
    // Permitimos enviar FormData (con archivos) o JSON plano (sin archivos)
    if (typeof FormData !== 'undefined' && data instanceof FormData) {
      const response = await this.api.post('/finance/expenses', data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data.data;
    }
    const response = await this.api.post('/finance/expenses', data);
    return response.data.data;
  }

  async updateExpense(id: number, data: any) {
    const response = await this.api.put(`/finance/expenses/${id}`, data);
    return response.data.data;
  }

  // Métodos para conductores
  async getDrivers(params?: any) {
    const response = await this.api.get('/drivers', { params });
    return response.data.data?.drivers || response.data.data || response.data || [];
  }

  async getDriver(id: number) {
    const response = await this.api.get(`/drivers/${id}`);
    return response.data.data;
  }

  async createDriver(data: FormData) {
    const response = await this.api.post('/drivers', data, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data.data;
  }

  async updateDriver(id: number, data: FormData) {
    try {
      const response = await this.api.post(`/drivers/${id}`, data, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'X-HTTP-Method-Override': 'PUT'
        }
      });
      return response.data.data;
    } catch (err) {
      const response = await this.api.put(`/drivers/${id}`, data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data.data;
    }
  }

  async deleteDriver(id: number) {
    const response = await this.api.delete(`/drivers/${id}`);
    return response.data;
  }

  async getActiveDrivers() {
    const response = await this.api.get('/drivers/active');
    return response.data.data;
  }

  // Métodos para clientes
  async getClients(params?: any) {
    const response = await this.api.get('/clients', { params });
    return response.data.data?.clients || response.data.data || response.data || [];
  }

  async getClient(id: number) {
    const response = await this.api.get(`/clients/${id}`);
    return response.data.data;
  }

  async createClient(data: any) {
    const response = await this.api.post('/clients', data);
    return response.data.data;
  }

  async updateClient(id: number, data: any) {
    const response = await this.api.put(`/clients/${id}`, data);
    return response.data.data;
  }

  async deleteClient(id: number) {
    const response = await this.api.delete(`/clients/${id}`);
    return response.data;
  }

  async getActiveClients() {
    const response = await this.api.get('/clients/active');
    return response.data.data;
  }

  async getClientStats(id: number) {
    const response = await this.api.get(`/clients/${id}/stats`);
    return response.data.data;
  }

  // Métodos para rutas
  async getRoutes(params?: any) {
    const response = await this.api.get('/routes', { params });
    return response.data.data?.routes || response.data.data || response.data || [];
  }

  async getRoute(id: number) {
    const response = await this.api.get(`/routes/${id}`);
    return response.data.data;
  }

  async createRoute(data: any) {
    const response = await this.api.post('/routes', data);
    return response.data.data;
  }

  async updateRoute(id: number, data: any) {
    const response = await this.api.put(`/routes/${id}`, data);
    return response.data.data;
  }

  async deleteRoute(id: number) {
    const response = await this.api.delete(`/routes/${id}`);
    return response.data;
  }

  // Métodos para planificación de rutas
  async planOptimizedRoute(data: any) {
    const response = await this.api.post('/route-planning/optimize', data);
    return response.data.data;
  }

  async generateMapsLink(waypoints: any[]) {
    const response = await this.api.post('/route-planning/maps-link', { waypoints });
    return response.data.data;
  }

  async calculateDistance(origin: string, destination: string) {
    const response = await this.api.post('/route-planning/calculate-distance', { origin, destination });
    return response.data.data;
  }

  async saveRoutePlan(data: any) {
    const response = await this.api.post('/route-planning/save', data);
    return response.data;
  }

  async startRoute(id: number) {
    const response = await this.api.patch(`/routes/${id}/start`);
    return response.data.data;
  }

  async completeRoute(id: number) {
    const response = await this.api.patch(`/routes/${id}/complete`);
    return response.data.data;
  }

  async getGoogleMapsLink(id: number) {
    const response = await this.api.get(`/routes/${id}/google-maps`);
    return response.data.data;
  }

  // Reportes
  async getReports(startDate: string, endDate: string) {
    const response = await this.api.get('/reports', { params: { start_date: startDate, end_date: endDate } });
    return response.data.data || response.data || {};
  }

  async getFinancialReport(startDate: string, endDate: string, groupBy: 'day' | 'week' | 'month' | 'year' = 'month') {
    const response = await this.api.get('/reports/financial', { params: { start_date: startDate, end_date: endDate, group_by: groupBy } });
    return response.data.data || response.data || {};
  }

  // Mantenimientos - Alertas
  async getMaintenanceAlerts() {
    const response = await this.api.get('/reports/maintenance/alerts');
    return response.data.alerts || response.data.data?.alerts || [];
  }

  async sendMaintenanceAlertsEmail(to?: string) {
    const response = await this.api.post('/reports/maintenance/alerts/email', { to });
    return response.data; // { success, sent, to, html_preview? }
  }

  async recomputeMaintenanceLastOdo() {
    const response = await this.api.post('/reports/maintenance/recompute');
    return response.data; // { success, updated }
  }

  // Configuración de usuario
  async getUserSettings() {
    const response = await this.api.get('/settings');
    return response.data.data;
  }

  async updateProfile(profileData: any) {
    const response = await this.api.put('/settings/profile', profileData);
    return response.data.data;
  }

  async updateSettings(settings: any) {
    const response = await this.api.put('/settings', settings);
    return response.data.data;
  }

  // Método genérico para health check
  async healthCheck() {
    const response = await this.api.get('/health');
    return response.data;
  }
}

export const saasApiService = new SaasApiService();
export default saasApiService;
